import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["indeterminate", "semanticColor", "id", "theme"];
import React, { useContext } from 'react';
import styled from 'styled-components';
import { KeyboardDetectionContext } from "../../contexts/KeyboardDetectionContext";
import { renamedPropWarning } from "../../deprecationWarnings";
import { rootStyle } from "../../styles";
import { Checkbox as FormCheckbox } from "../FormCheckbox/Checkbox";
import { Indicator as FormIndicator } from "../FormCheckbox/Indicator";
import { VisuallyHidden } from "../VisuallyHidden";
var Checkbox = styled(FormCheckbox).withConfig({
  displayName: "TableCheckbox__Checkbox",
  componentId: "dfldaq-0"
})(["min-block-size:0;padding:0;label{line-height:0;}"]);
var Indicator = styled(FormIndicator).withConfig({
  displayName: "TableCheckbox__Indicator",
  componentId: "dfldaq-1"
})(["", ";top:0;"], rootStyle());
export var TableCheckbox = /*#__PURE__*/React.forwardRef(function TableCheckbox(_ref, ref) {
  var indeterminate = _ref.indeterminate,
      _ref$semanticColor = _ref.semanticColor,
      semanticColor = _ref$semanticColor === void 0 ? 'essentialBrightAccent' : _ref$semanticColor,
      id = _ref.id,
      theme = _ref.theme,
      props = _objectWithoutProperties(_ref, _excluded);

  if (theme) {
    renamedPropWarning('theme', 'semanticColor');
  }

  var _useContext = useContext(KeyboardDetectionContext),
      isUsingKeyboard = _useContext.isUsingKeyboard;

  return /*#__PURE__*/React.createElement(Checkbox, null, /*#__PURE__*/React.createElement(VisuallyHidden, Object.assign({
    component: "input",
    type: "checkbox",
    ref: ref,
    id: id
  }, props)), /*#__PURE__*/React.createElement("label", {
    htmlFor: id
  }, /*#__PURE__*/React.createElement(Indicator, {
    className: indeterminate ? 'Encore_indeterminate' : '',
    indeterminate: indeterminate,
    isUsingKeyboard: isUsingKeyboard,
    semanticColor: semanticColor
  })));
});
/**
* **TableCheckbox** ![Status: Production](https://img.shields.io/badge/PRODUCTION-%2357B560|height=14)
*
* [GitHub](https://ghe.spotify.net/encore/web/tree/master/src/core/components/TableCheckbox) | [Storybook](https://encore-web.spotify.net/?path=/docs/components-table-tablecheckbox--stateful) | [Encore](https://encore.spotify.net/web/components/table?format=figma-figma#multi-select) | [Figma](https://www.figma.com/file/bnQXqGkLeiTlbk8JXGDcsX/Encore-Web---Light-Theme-Toolkit?node-id=561:30) |
*
* TableCheckbox renders checkboxes within cells, to be used at the beginning of a row to allow users to select multiple rows.
*
* @example
* () => {
*  const [row1, toggleRow1] = useState(false);
*  const [row2, toggleRow2] = useState(false);
*  const indeterminate = (row1 || row2) && (!row1 || !row2);
*  const checked = row1 && row2;
*
*  const headerCheckboxToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
*    if (e.target.checked) {
*      toggleRow1(true);
*      toggleRow2(true);
*    } else {
*      toggleRow1(false);
*      toggleRow2(false);
*    }
*  };
*
*  return (
*    <Table>
*      <colgroup>
*        <col width="" />
*        <col width="25%" />
*        <col width="25%" />
*        <col width="25%" />
*        <col width="25%" />
*      </colgroup>
*      <thead>
*        <TableRow>
*          <TableHeaderCell>
*            <TableCheckbox
*              id="stateful-header-1"
*              name="stateful-checkbox-example-header"
*              indeterminate={indeterminate}
*              checked={checked}
*              onChange={(e: React.ChangeEvent<HTMLInputElement>) => headerCheckboxToggle(e)}
*            />
*          </TableHeaderCell>
*          <TableHeaderCell>Header</TableHeaderCell>
*          <TableHeaderCell>Header</TableHeaderCell>
*          <TableHeaderCell>Header</TableHeaderCell>
*          <TableHeaderCell>Header</TableHeaderCell>
*        </TableRow>
*      </thead>
*      <tbody>
*        <TableRow hover select={row1}>
*          <TableCell>
*            <TableCheckbox
*              id="stateful-row-1"
*              name="stateful-checkbox-example"
*              checked={row1}
*              onChange={() => toggleRow1(!row1)}
*            />
*          </TableCell>
*          <TableCell>Cell</TableCell>
*          <TableCell>Cell</TableCell>
*          <TableCell>Cell</TableCell>
*          <TableCell>Cell</TableCell>
*        </TableRow>
*        <TableRow hover select={row2}>
*          <TableCell>
*            <TableCheckbox
*              id="stateful-row-2"
*              name="stateful-checkbox-example-1"
*              checked={row2}
*              onChange={() => toggleRow2(!row2)}
*            />
*          </TableCell>
*          <TableCell>Cell</TableCell>
*          <TableCell>Cell</TableCell>
*          <TableCell>Cell</TableCell>
*          <TableCell>Cell</TableCell>
*        </TableRow>
*      </tbody>
*    </Table>
*  );
*};
*
*/

export default TableCheckbox;